/* @flow */

import React from 'react';
import glamorous from 'glamorous';
import { padding } from 'polished';
import desktopSvg from 'app/images/howtoplay/desktop.svg';
import phoneImage from 'app/images/howtoplay/phone.png';
import ResponsiveImage from 'app/components/commons/ResponsiveImage';
import SimpleMarkdownText from 'app/components/static/SimpleMarkdownText';

type Props = {
  description: string,
  frame: boolean,
  images: Array<string>,
  innerRef: Object => void,
  title: string,
  video?: string
};

const Description = glamorous.div(props => ({
  fontWeight: props.theme.fontWeight.normal,
  letterSpacing: '0.1em',
  marginBottom: '2rem',
  maxWidth: 800,
  textAlign: 'center',

  ' strong': {
    fontWeight: props.theme.fontWeight.demiBold
  }
}));

const Figure = glamorous.figure(
  {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    display: 'flex',
    justifyContent: 'center',
    margin: 0
  },
  props =>
    props.frame
      ? {
          backgroundImage: `url(${phoneImage})`,
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '500px auto',
          height: 500,
          paddingTop: 39,
          [props.theme.mediaQuery.desktopAndUp]: {
            backgroundImage: `url(${desktopSvg})`,
            paddingTop: 19
          }
        }
      : {}
);

const Pane = glamorous.div(props => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  ...padding('1rem', props.theme.spacing.medium)
}));

const Title = glamorous.h2(props => ({
  borderBottomColor: props.theme.color.primary,
  borderBottomStyle: 'solid',
  borderBottomWidth: 2,
  display: 'flex',
  textAlign: 'center',
  fontSize: props.theme.fontSize.large,
  fontWeight: props.theme.fontWeight.normal,
  letterSpacing: '0.1em',
  marginBottom: '1.5rem',
  ...padding('0', '1rem', '1rem')
}));

const VideoWrapper = glamorous.div(() => ({
  maxWidth: 800
}));

const StyledResponsiveImage = glamorous(ResponsiveImage)(({ theme }) => ({
  [theme.mediaQuery.desktopAndUp]: {
    height: 262
  }
}));

function HowToPlayPane({ description, frame, images, innerRef, title, video }: Props) {
  return (
    <Pane innerRef={innerRef}>
      <Title>{title}</Title>
      {description && (
        <Description>
          <SimpleMarkdownText source={description} />
        </Description>
      )}
      {video && (
        <VideoWrapper>
          <video width="100%" height="auto" controls>
            <source src={video} type="video/mp4" />
            <track kind="captions" />
          </video>
        </VideoWrapper>
      )}
      {images && (
        <Figure frame={frame}>
          <StyledResponsiveImage images={images} />
        </Figure>
      )}
    </Pane>
  );
}

HowToPlayPane.defaultProps = {
  video: undefined
};

export default HowToPlayPane;
